import { sql } from 'drizzle-orm'
import {
  type AnyPgColumn,
  bigint,
  boolean,
  check,
  date,
  index,
  integer,
  jsonb,
  pgEnum,
  pgTable,
  smallint,
  text,
  timestamp,
  uniqueIndex,
} from 'drizzle-orm/pg-core'

import { user } from './auth.ts'
import { createdAt, primaryId, taxRateBps, updatedAt } from './columns.ts'
import { deliveryMethod } from './delivery.ts'
import { offering, offeringVariant } from './offerings.ts'
import { trainingSession } from './training.ts'

/**
 * Order lifecycle. Complete from the start because pgEnum values are
 * append-only in migrations and a half-set would force a migration per phase.
 *
 *  P6  writes `PENDING` (order captured, no gateway involved).
 *  Ppay moves `PENDING → AWAITING_PAYMENT → PAID | FAILED`.
 *  Staff move `PAID → FULFILLED`, or `→ CANCELLED` / `→ REFUNDED`.
 */
export const orderStatus = pgEnum('order_status', [
  'PENDING',
  'AWAITING_PAYMENT',
  'PAID',
  'CANCELLED',
  'FAILED',
  'FULFILLED',
  'REFUNDED',
])

/**
 * Fulfilment lifecycle of ONE line — the spec's "tor pozycji".
 *
 * Deliberately a SECOND axis, orthogonal to `orderStatus` above: that one is
 * the money (has this basket been paid for?), this one is the work (where is
 * this single thumbnail up to?). One payment can carry ten lines that finish
 * weeks apart, each with its own deadline, performer and chat, so a single
 * status on the header cannot express it. The umbrella is Completed only once
 * every line is — derived, never stored twice (A3).
 *
 * English identifiers per AGENTS.md; the spec's Polish tab labels map 1:1 and
 * are pinned here so the two can never drift:
 *
 *  `PENDING`           — Oczekujące.     Paid, performer has not replied yet.
 *  `IN_PROGRESS`       — W realizacji.   Performer replied. THE BOUNDARY: past
 *                        it there is no refund and the brief is frozen.
 *  `AWAITING_APPROVAL` — Do akceptacji.  Delivered. The delivery clock pauses.
 *  `COMPLETED`         — Zakończone.     Accepted, auto-accepted or closed out.
 *                        Performer earnings are counted from here.
 *  `CANCELLED`         — Anulowane.      Refunded out of `PENDING`, or dropped
 *                        with its parent. Never blocks the umbrella closing.
 */
/**
 * Where a film's LENGTH conversation stands (spec §8 · G6).
 *
 * Spec §8 puts a whole stage in front of the content revisions: the editor
 * makes a rough cut, the client sees how many minutes it came out at, and
 * decides — accept the length (and pay for any minutes over the band's floor),
 * or point at what to cut. Only then does anybody add music, effects or colour.
 *
 * The point of the stage is CONTROL OF THE BILL. The surcharge is settled while
 * the expensive half of the work has not happened yet, so a client who does not
 * want to pay for the extra eight minutes can still have them removed. Doing it
 * at the end would be a bill nobody could do anything about.
 *
 *  `NONE`         — not a film, or no cut exists yet.
 *  `AWAITING_V1`  — Wersja 1 is with the CLIENT, 72 hours to answer.
 *  `RECUT`        — they asked for cuts; the editor is making Wersja 2.
 *  `SETTLED`      — the length is agreed and any surcharge is raised. Content
 *                   revisions (G2) start from here, with both rounds intact:
 *                   spec §8 says cutting minutes "nie zabiera limitu 2 tur".
 */
export const filmLengthStage = pgEnum('film_length_stage', [
  'NONE',
  'AWAITING_V1',
  'RECUT',
  'SETTLED',
])

export const orderItemStatus = pgEnum('order_item_status', [
  'PENDING',
  'IN_PROGRESS',
  'AWAITING_APPROVAL',
  'COMPLETED',
  'CANCELLED',
])

/**
 * A placed order.
 *
 * Every money column is NET grosze (`bigint`), plus a single gross total so an
 * invoice and a payment request never have to re-derive VAT from a float.
 * Totals are RECOMPUTED server-side from the catalogue at checkout — the
 * client-submitted price is never trusted — and then frozen here, together
 * with the label snapshots on each line, so a later price change cannot
 * rewrite what somebody already agreed to pay.
 *
 * `userId` is nullable on purpose: guest checkout is a supported path, and a
 * guest order still has to exist and be fulfillable.
 */
export const order = pgTable(
  'order',
  {
    id: primaryId('ord'),
    /** Human-facing reference, e.g. `FLOU-2026-0042`. Unique, never reused. */
    orderNumber: text('order_number').notNull(),
    userId: text('user_id').references(() => user.id, { onDelete: 'set null' }),

    /**
     * Set only on an order invented during a training rehearsal (`training_session`).
     *
     * NULL on every real order, which is what makes „is this real?" one column
     * read rather than a join, and `CASCADE` so „Zakończ test" takes the order
     * and everything hanging off it out with the rehearsal.
     *
     * It is a GUARD, not a label. `notify`, the payment path and the invoice
     * path each ask for it before they do anything the outside world can see —
     * a rehearsal that sent a real customer a real letter would be worse than
     * no rehearsal at all. The badge in the UI reads the same column, so the
     * thing that is marked on screen and the thing that is blocked in the
     * server are the same fact.
     */
    trainingSessionId: text('training_session_id').references(() => trainingSession.id, {
      onDelete: 'cascade',
    }),

    status: orderStatus('status').notNull().default('PENDING'),

    /** Which brand surface the basket was built on (`tworca` / `firma`). */
    brand: text('brand'),
    locale: text('locale').notNull().default('pl'),

    // ── contact snapshot (frozen at checkout, independent of the account) ──
    customerFirstName: text('customer_first_name'),
    customerLastName: text('customer_last_name'),
    customerEmail: text('customer_email').notNull(),
    customerPhone: text('customer_phone'),
    companyName: text('company_name'),
    /** Polish NIP, digits only. */
    taxId: text('tax_id'),
    addressLine1: text('address_line1'),
    addressLine2: text('address_line2'),
    postalCode: text('postal_code'),
    city: text('city'),
    country: text('country').notNull().default('PL'),

    // ── money (net grosze unless the name says otherwise) ─────────────────
    currency: text('currency').notNull().default('PLN'),
    /** Sum of priced lines before basket-level discounts. */
    subtotalNetMinor: bigint('subtotal_net_minor', { mode: 'number' }).notNull().default(0),
    /** Everything knocked off: per-line ladders plus the basket bonus. */
    discountNetMinor: bigint('discount_net_minor', { mode: 'number' }).notNull().default(0),
    totalNetMinor: bigint('total_net_minor', { mode: 'number' }).notNull().default(0),
    totalGrossMinor: bigint('total_gross_minor', { mode: 'number' }).notNull().default(0),
    /**
     * Header rate, kept for the single-rate invoice. It is a SUMMARY of the
     * lines, not their source: each `order_item` freezes its own
     * `taxRateBps`, so a basket that mixes rates stays representable and a
     * later catalogue rate change cannot rewrite the tax on a placed order.
     * Derive this from the lines at checkout; never the other way round.
     */
    taxRateBps: taxRateBps(),
    /** MULTI_SERVICE rung applied to this basket, in basis points. */
    multiServiceDiscountBps: smallint('multi_service_discount_bps').notNull().default(0),
    /**
     * Set when at least one line is `QUOTE`. Those lines are excluded from
     * every total AND from the distinct-service count that earns the basket
     * bonus — a line with no price cannot buy a percentage off.
     *
     * **P6 OBLIGATION, not a database rule.** This is the one invariant in the
     * order tables that a CHECK cannot hold: a row-level constraint cannot see
     * sibling rows, so nothing here stops `false` on a header whose lines
     * include a quote-only one — verified insertable. The flag is what will
     * gate "do not charge this basket" in P6/Ppay, so a stale `false` is a
     * charge attempt against a basket with no price.
     *
     * P6 must therefore RECOMPUTE it in the same transaction that writes the
     * lines (`hasQuoteOnlyItems = lines.some(l => l.isQuoteOnly)`), never carry
     * it over from the client, and pin that with a test. If P6 would rather not
     * own it, the alternative is a statement-level trigger — but one of the two
     * has to happen; do not read this comment as an enforced guarantee.
     */
    hasQuoteOnlyItems: boolean('has_quote_only_items').notNull().default(false),

    /**
     * How the equipment in this basket travels (spec §11 · I6).
     *
     * The customer's choice, frozen at checkout: one per BASKET, because that
     * is how spec §11 words it ("wybór w koszyku") and because two cameras
     * going to the same address by different routes is not a thing anybody
     * asked for. NULL for a basket with nothing to deliver.
     *
     * Each rental booking carries its own copy — see `booking.delivery_method`
     * — since that is where the buffer and the hand-over clock live. This one
     * is the record of what was agreed.
     */
    deliveryMethod: deliveryMethod('delivery_method'),
    /**
     * Spec §11: "Checkbox akceptacji regulaminu najmu wymagany."
     *
     * A moment rather than a boolean, like every other consent in this schema:
     * a claim about what somebody agreed to is worth what the timestamp is
     * worth. Asked only when the basket holds a hire, and refused server-side —
     * a tick that can be skipped by posting the form yourself says nothing.
     */
    rentalTermsAcceptedAt: timestamp('rental_terms_accepted_at', { withTimezone: true }),

    notes: text('notes'),
    /** Free-form snapshot of anything the checkout collected but does not model. */
    metadata: jsonb('metadata').$type<Record<string, unknown>>(),

    /**
     * The pricing rules in force when this order was placed (Cennik → Reguły).
     *
     * A column of its own rather than a corner of `metadata`, because this one
     * is READ BACK to decide money — the ceiling on an extra revision, months
     * after the basket was priced — and a figure that settles an argument
     * should not live in the bag marked "anything we did not model". Nullable:
     * orders placed before the rules became editable fall through to the live
     * values, which is the rule they were always judged by.
     */
    pricingRulesSnapshot: jsonb('pricing_rules_snapshot').$type<Record<string, number>>(),

    // ── consent (see `lead` for the same pattern on forms) ────────────────
    termsAcceptedAt: timestamp('terms_accepted_at', { withTimezone: true }),
    privacyAcceptedAt: timestamp('privacy_accepted_at', { withTimezone: true }),

    placedAt: timestamp('placed_at', { withTimezone: true }),
    paidAt: timestamp('paid_at', { withTimezone: true }),
    cancelledAt: timestamp('cancelled_at', { withTimezone: true }),
    fulfilledAt: timestamp('fulfilled_at', { withTimezone: true }),

    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => [
    uniqueIndex('order_number_unique').on(t.orderNumber),
    index('order_user_idx').on(t.userId, t.createdAt),
    index('order_status_idx').on(t.status, t.createdAt),
    index('order_email_idx').on(t.customerEmail),
    // Every read that asks „which orders belong to this rehearsal?" — the test
    // desk's own list and the teardown's verification — filters on this one
    // column, and every real-order read filters it OUT.
    index('order_training_idx').on(t.trainingSessionId),

    // The header carries the same class of invariant as `order_item` below,
    // and for a stronger reason: these are the numbers an invoice and a payment
    // request are built from. Without them the catalogue was guarded and the
    // ORDER was not — the one row a customer is actually charged against.
    //
    // `>= 0`, not `> 0`: a quote-only basket legitimately totals zero (every
    // line is "Wycena indywidualna"), and so does a 100% rung. Negative is the
    // thing that has no meaning — it is FLOU paying the customer.
    check(
      'order_totals_nonneg',
      sql`${t.subtotalNetMinor} >= 0
          AND ${t.discountNetMinor} >= 0
          AND ${t.totalNetMinor} >= 0
          AND ${t.totalGrossMinor} >= 0`,
    ),
    // `vatMinor()` in `money.ts` is defined as gross − net and documented as
    // "the tax that was really applied". Nothing else pins the sign of that
    // subtraction, so without this a gross below net makes the single source
    // of VAT return a NEGATIVE tax onto an invoice.
    check('order_gross_covers_net', sql`${t.totalGrossMinor} >= ${t.totalNetMinor}`),
    // `multi_service_discount_bps` is `smallint`, so the representable range
    // runs −32768..32767: without this, a negative value is a SURCHARGE wearing
    // a discount's name and 32000 is a 320% one. Same range as
    // `order_item_discount_range`; the header rung had no analogue.
    check(
      'order_multi_service_bps_range',
      sql`${t.multiServiceDiscountBps} >= 0 AND ${t.multiServiceDiscountBps} <= 10000`,
    ),
    check('order_tax_rate_range', sql`${t.taxRateBps} >= 0 AND ${t.taxRateBps} <= 10000`),
  ],
)

/**
 * One basket line — a montage package, three days of a camera, an hour of a
 * room. All of them, because there is one catalogue.
 *
 * Both catalogue keys are nullable and `set null`, so deleting a discontinued
 * offering cannot cascade a paid order out of existence; the label, unit and
 * price snapshots below keep the line readable and refundable afterwards.
 *
 * That `set null` is why `order_item_variant_needs_offering` is written as an
 * implication rather than an equivalence. `ON DELETE SET NULL` fires as an
 * UPDATE, which re-checks the row: requiring a variant to always be present
 * would turn deleting a catalogue row into a constraint violation instead of
 * the intended detach.
 */
export const orderItem = pgTable(
  'order_item',
  {
    id: primaryId('oitem'),
    orderId: text('order_id')
      .notNull()
      .references(() => order.id, { onDelete: 'cascade' }),
    /**
     * What was bought, and in which variant. Two nullable keys where there used
     * to be five plus a `kind` enum: with one catalogue the category answers
     * "what sort of line is this?", and a quote-option line is simply an
     * offering with no variant and `isQuoteOnly`.
     *
     * `SET NULL` on both, and every label and price below is a snapshot, so
     * deleting a discontinued offering loses the catalogue link and nothing
     * else — the order still reads exactly as it was placed.
     */
    offeringId: text('offering_id').references(() => offering.id, { onDelete: 'set null' }),
    offeringVariantId: text('offering_variant_id').references(() => offeringVariant.id, {
      onDelete: 'set null',
    }),

    // ── snapshots, so a renamed variant does not rewrite history ──────────
    label: text('label').notNull(),
    variantLabel: text('variant_label'),
    unitLabel: text('unit_label'),

    /**
     * NET unit price in grosze. NULL **iff** `isQuoteOnly`, enforced by
     * `order_item_price_quote_xor` below.
     */
    unitPriceNetMinor: bigint('unit_price_net_minor', { mode: 'number' }),
    isQuoteOnly: boolean('is_quote_only').notNull().default(false),
    /**
     * VAT rate frozen from the catalogue row this line was built from, in
     * basis points. Per LINE, not per order, for the reason `taxRateBps` in
     * `columns.ts` gives: a rate change must not rewrite the tax on work that
     * was already ordered. It also makes a mixed-rate basket representable —
     * with only the header rate, one of the two rates would have to be lost.
     */
    taxRateBps: taxRateBps(),
    quantity: integer('quantity').notNull().default(1),
    /** Per-line ladder rung that was applied (VOLUME or RENTAL_DAYS). */
    discountBps: smallint('discount_bps').notNull().default(0),
    /** `unitPrice × quantity` after `discountBps`. NULL for quote-only lines. */
    lineTotalNetMinor: bigint('line_total_net_minor', { mode: 'number' }),

    /**
     * What the performer is paid for ONE unit of this line, NET grosze,
     * frozen when the order is paid for (spec §17 · J1).
     *
     * A SNAPSHOT, beside `unitPriceNetMinor` and for the identical reason: the
     * price list is retuned and the payout for work somebody already did must
     * not move with it. Spec §17 makes the rate an Admin's manual decision, so
     * it changes exactly as often as somebody decides to change it — which is
     * the case a frozen column exists for.
     *
     * Frozen at PAYMENT, not at checkout. Before the money lands there is no
     * job: an unpaid basket expires, and stamping a payout onto it would put
     * rows in the earnings report for work nobody ever ordered.
     *
     * Per UNIT, mirroring the price column above, so the two divide and
     * multiply the same way. The payout for the line is `rate × quantity` and
     * the margin is `lineTotalNetMinor − payout` — both derived, never stored,
     * because a stored margin is a third number that can disagree with the two
     * it came from.
     *
     * NULL means nobody is paid per unit here: a hire, a room, a coordinator
     * line, or a variant whose rate an Admin has not set.
     */
    performerRateNetMinor: bigint('performer_rate_net_minor', { mode: 'number' }),

    /**
     * What the person who was booked is owed for a reservation that was called
     * off, NET grosze (umowa · punkt 20).
     *
     * > za fotografem czy operatorem stoi człowiek który dostaje 0/25/50 przy
     * > odwołaniu rezerwacji czyli połowa tego co flou
     *
     * NULL on every line that is not a cancelled shoot day, which is almost all
     * of them: a piece of work pays through `performerRateNetMinor` above, and a
     * camera or a room has nobody standing behind it to be compensated.
     *
     * Stored rather than derived, and that is the whole reason it is a column.
     * The figure is `performerCancellationMinor(lineTotal, tier)`, and the TIER
     * is a function of the instant the line was cancelled — so a live
     * computation would answer differently every day the reservation's start
     * date recedes further into the past. It is stamped once, by the act that
     * cancels, and from then on it is the answer.
     *
     * A cancelled line is otherwise worth nothing to a performer, which is why
     * the month's close cannot simply add this into `LINE_PAYOUT`: that sum
     * filters on `COMPLETED`, and this is the one payout that belongs to a line
     * which will never be completed. See `closePerformerStatements`.
     */
    cancellationPayoutNetMinor: bigint('cancellation_payout_net_minor', { mode: 'number' }),

    /**
     * When that fee was stamped — and therefore which month pays it.
     *
     * Its own column because `order_item` has no `cancelled_at`: the ORDER has
     * one, but a single line is cancellable while the rest of the order carries
     * on, so the header's date would bucket a September cancellation into
     * whatever month the order was eventually closed in. `updated_at` is worse
     * still — any later write to the row would move the money between periods.
     *
     * A statement is frozen per calendar month (`performer_statement`), so the
     * instant a payout belongs to has to be a fact that never moves once
     * written. This is that instant.
     */
    cancellationPayoutAt: timestamp('cancellation_payout_at', { withTimezone: true }),

    /**
     * What the customer asked for, in their own words (spec §6 · G1).
     *
     * Frozen onto the line at checkout and editable only while the line is
     * `PENDING` — `isBriefEditable` in `@repo/core` is the one predicate, and it
     * is the same boundary the refund dies at (spec §1). Past it the performer
     * has read the brief and started work, so a silent rewrite would change the
     * job under them.
     *
     * Nullable because most of the catalogue has nothing to brief: a camera
     * hire is a date, not a request. `offering.brief_mode` is what decides
     * whether a given line may leave this NULL.
     *
     * Copied to every piece when a multi-piece line is split into one row per
     * piece (`splitPerPiece`): ten thumbnails ordered together are ten tracks
     * against one brief, and each track then refines it in its own chat.
     */
    brief: text('brief'),

    /**
     * Where the raw material is (spec §7 · G4).
     *
     * A LINK and never an upload — Google Drive, WeTransfer, whatever the
     * client already uses. Spec §7 is explicit ("bez uploadu plików"), and the
     * reason is not laziness: a montage order is tens of gigabytes, and a
     * product that stored them would be a storage business with an editing
     * side-line.
     *
     * NULL when the offering asks for none (`offering.source_mode`). Editable
     * past spec §1's boundary, unlike the brief, and that asymmetry is
     * deliberate: a wrong link is the one thing the client MUST be able to fix
     * after work has started, because until they do there is no work to do.
     */
    sourceUrl: text('source_url'),
    /**
     * When the buyer ticked "link i materiały są prawidłowe" (spec §7).
     *
     * A timestamp and not a boolean, because what it records is a declaration
     * made at a moment: spec §7 hangs real consequences off it ("jeśli materiał
     * wadliwy: to problem klienta, brak roszczeń z tytułu opóźnienia"), and a
     * claim with no date behind it is not evidence of anything.
     *
     * Re-stamped when the client supplies a REPLACEMENT link, because that is a
     * fresh declaration about fresh material.
     */
    materialConfirmedAt: timestamp('material_confirmed_at', { withTimezone: true }),
    /**
     * When the performer said there is nothing here to work with (spec §7).
     *
     * The instant, beside `flags.badMaterial` which is what the auto-acceptance
     * sweep reads. Two representations of one fact, and each earns its place:
     * the flag is the predicate every screen and sweep asks, the timestamp is
     * the start of the 90-day window the client has to fix it. Written in the
     * same statement, always.
     *
     * Cleared when a replacement link arrives — the window is not something a
     * client should still be inside once they have done what was asked.
     */
    badMaterialAt: timestamp('bad_material_at', { withTimezone: true }),

    // ── length, for a film billed by the minute (spec §8 · G5) ───────────
    // All four NULL for everything that is not a film. Frozen at checkout like
    // the price beside them: the band is what was BOUGHT, and a catalogue edit
    // must not move the ceiling on a film already being cut.
    /** The band's floor — the minutes actually paid for up front. */
    lengthBandMin: smallint('length_band_min'),
    /** The band's ceiling — spec §8's hard limit on the finished film. */
    lengthBandMax: smallint('length_band_max'),
    /**
     * Minutes charged on this line. Equal to `length_band_min` today and stored
     * anyway, because it is the number the invoice is made of: reading it back
     * off the band would mean re-deriving money from a rule that may change.
     */
    paidMinutes: smallint('paid_minutes'),
    /**
     * How long the cut actually came out (G6 writes it).
     *
     * NULL until the film exists. The gap between this and `paid_minutes` is
     * what the overage surcharge is computed from — never at the end, but at
     * Wersja 1, which is spec §8's whole point about giving the client control
     * of the bill before the expensive half of the work happens.
     */
    finalMinutes: smallint('final_minutes'),
    /**
     * Where the LENGTH conversation stands (spec §8 · G6).
     *
     * A stage of its own and not a value of `status`, because it is a stage of
     * its own: spec §8 calls it "osobny etap PRZED turami poprawek
     * merytorycznych", and folding it into the item's four tabs would mean a
     * client asked to approve a film that has no music in it yet.
     *
     * `NONE` covers everything that is not a film, and a film before the first
     * cut exists. See {@link filmLengthStage}.
     */
    lengthStage: filmLengthStage('length_stage').notNull().default('NONE'),
    /**
     * End of the client's 72 hours to answer Wersja 1 (spec §8).
     *
     * Its own column beside `acceptance_due_at` rather than sharing it: the two
     * windows can be open in sequence on the same line, they close on different
     * outcomes, and one column would make "which decision is this counting down
     * to" unanswerable. NULL whenever no length decision is pending.
     */
    lengthDecisionDueAt: timestamp('length_decision_due_at', { withTimezone: true }),

    // ── raw footage (spec §8 "Limit surowego materiału" · G7) ────────────
    /**
     * How many minutes of tape the client SAID they were sending.
     *
     * A declaration and not a measurement — spec §8 puts the responsibility on
     * the client in as many words ("odpowiedzialność po stronie klienta"), and
     * the editor reporting it as short is a separate, human step. NULL for
     * anything that needs no source material.
     */
    footageMinutes: smallint('footage_minutes'),
    /**
     * The allowance this line was sold with, frozen.
     *
     * Derivable — the band's ceiling × 2.5, or 10 per reel — and stored anyway,
     * for the reason every frozen column here is stored: the RULE may change,
     * and a line has to keep reading back as the deal that was struck.
     */
    footageLimitMinutes: smallint('footage_limit_minutes'),
    /**
     * What the minutes over that limit cost, NET grosze, already inside
     * `line_total_net_minor`.
     *
     * Recorded separately because the line total otherwise stops being
     * explainable: "20 × 125 zł" and a total of 2730 zł need this number in
     * between them. Charged up front (spec §8), never laddered — see
     * `OrderLineInput.extraNetMinor` for why no discount may touch it.
     */
    footageSurchargeNetMinor: bigint('footage_surcharge_net_minor', { mode: 'number' }),

    /**
     * The line this one is CUT FROM (spec §8 "Rolki z filmu" · G8).
     *
     * Set on a reel the client asked to have made out of a film in the same
     * order. It is one column and it carries four separate consequences, which
     * is the entire reason it is a reference rather than a boolean:
     *
     *   MATERIAL   The reel brings no tape of its own — it inherits the film's,
     *              so it needs neither a `source_url` nor a footage
     *              declaration, and the surcharge is paid ONCE, on the film.
     *   ORDER      "Ich termin startuje po ukończeniu filmu." A dependent line
     *              is paid for like any other and gets NO performer, NO slot and
     *              NO `due_at` until the film completes — see `startFulfilment`.
     *   PERSON     "Rolki z filmu robi ten sam montażysta co film", so when the
     *              release happens the assignment is copied, not competed for.
     *   DECAY      A cancelled film takes its reels with it (refunded); a film
     *              lost to bad material sets them free to bring their own tape.
     *
     * `SET NULL`, which is exactly the second decay path written as a
     * constraint: losing the film must never delete the reel somebody paid for
     * — it must leave it standing on its own.
     *
     * Self-referential, and deliberately one level deep. Nothing enforces that
     * in the schema (a CHECK cannot see another row), so `dependencyProblem`
     * refuses a link whose target already has one: a chain would make "when does
     * this line start" a graph walk, and the spec describes a pair.
     */
    dependsOnItemId: text('depends_on_item_id').references((): AnyPgColumn => orderItem.id, {
      onDelete: 'set null',
    }),

    /** Rental dates, studio window, picked quote options — whatever the kind needs. */
    metadata: jsonb('metadata').$type<Record<string, unknown>>(),
    sortOrder: integer('sort_order').notNull().default(0),

    // ── the line's own track (spec §1, §4) ────────────────────────────────
    // Everything below is written by the fulfilment side, never by pricing.
    // A line is born `PENDING` at the moment the order is marked paid (F5),
    // which is also when the performer, the deadline and the chat appear.
    status: orderItemStatus('status').notNull().default('PENDING'),
    /**
     * Who is doing the work. `SET NULL` rather than cascade: a performer
     * leaving FLOU must not take the order line with them — the row stays,
     * reads back, and can be reassigned.
     */
    assignedUserId: text('assigned_user_id').references(() => user.id, { onDelete: 'set null' }),

    /** When the performer owes the work. NULL until the line is paid for. */
    dueAt: timestamp('due_at', { withTimezone: true }),
    /**
     * When the CLIENT was promised the work — `due_at` plus FLOU's margin at
     * this level (`offering_variant.client_turnaround_hours`). NULL on a level
     * with no separate promise, where the client's date IS `due_at`.
     */
    clientDueAt: timestamp('client_due_at', { withTimezone: true }),
    /**
     * WHOSE DAY this line is holding — the `capacity_day` row it took a slot
     * from when the money landed (B4).
     *
     * Written beside `assigned_user_id` and for a reason that only shows up
     * later: a refund has to give the slot back (spec §6, "slot wraca do
     * puli"), and `release_slot` is keyed on (user, variant, DAY). Without this
     * the day would have to be reverse-engineered from `due_at`, which works
     * for a montage line (stored as the following midnight) and does not for a
     * thumbnail (24 h from payment, which can land on either side of a day
     * boundary). A guess here returns a slot to the wrong day and quietly
     * oversells one while under-selling another.
     *
     * NULL before payment, and on a quote-option line that never took capacity.
     */
    deliveryDay: date('delivery_day', { mode: 'string' }),
    /** First performer reply — the `PENDING → IN_PROGRESS` boundary. */
    startedAt: timestamp('started_at', { withTimezone: true }),
    /** "Wyślij do akceptacji" — the moment the client's window opens. */
    deliveredAt: timestamp('delivered_at', { withTimezone: true }),
    /** End of the client's acceptance window: 24h first time, 12h after a revision. */
    acceptanceDueAt: timestamp('acceptance_due_at', { withTimezone: true }),
    acceptedAt: timestamp('accepted_at', { withTimezone: true }),

    /**
     * Revision rounds used. Two are free; asking a third accepts the work
     * outright (spec §6), which is why the CHECK stops at 3 rather than
     * leaving it open — an unbounded counter here would mean the rule is
     * enforced only in application code.
     */
    revisionRound: smallint('revision_round').notNull().default(0),

    /**
     * How many times the client has sent this back as a DEFECT rather than as a
     * revision round (umowa · punkt 13).
     *
     * ── WHY THE TWO ARE COUNTED APART ────────────────────────────────────
     * They are different claims about whose fault it is. A round is "I have
     * changed my mind", and the agreement puts its cost on the client — two are
     * included and the third accepts the work. A defect is "this is not what
     * was ordered", and nobody agreed to pay for delivering the wrong thing, so
     * it does not spend a round.
     *
     * Which is exactly why it is abusable, and why this is a COLUMN rather than
     * a flag. The owner's instruction was to have somebody look: "jak ktoś
     * wskazuje wadę to admin koordynator też dostaje powiadomienie żeby to
     * przenalizować czy nie jest to wymuszenie dodatkowej tury." A notification
     * answers "did this just happen"; only a count on the line answers the
     * question the person reading it actually has — "is this the third time?"
     *
     * The ceiling is generous and is a backstop, not a policy: the control is a
     * human reading the notice, and a line that has reached twenty is a
     * conversation that stopped being about this line long ago.
     */
    defectClaims: smallint('defect_claims').notNull().default(0),

    /**
     * When the performer declared they had deleted their local copies of the
     * client's material (umowa · punkt 16).
     *
     * ── WHY A DECLARATION AND NOT A DELETION ─────────────────────────────
     * Because FLOU cannot reach a laptop. The agreement requires the performer
     * to remove what they were given once the job is done, and the only thing
     * any system can hold is the moment they said they had. That is not
     * nothing: it is dated, it is theirs, and it is the difference between "we
     * assume everybody complies" and a record naming a person and a day.
     *
     * NULL means undeclared, which is a real and ordinary state — a line
     * finished an hour ago has not reached this yet. It is not evidence of
     * anything until somebody looks at how long it has been NULL, which is
     * exactly what the admin's list is for.
     */
    copiesDeletedAt: timestamp('copies_deleted_at', { withTimezone: true }),
    /**
     * Who said so. Stored beside the instant rather than assumed to be the
     * assignee, because a line can be reassigned after it closes and the
     * declaration belongs to whoever actually made it.
     */
    copiesDeletedByUserId: text('copies_deleted_by_user_id').references(() => user.id, {
      onDelete: 'set null',
    }),

    /**
     * How many rounds THIS line was sold with (Zalacznik 1 poz. 6).
     *
     * Frozen at checkout off `offering_variant.revision_rounds_included`, like
     * the brief and the length band beside it — a level retuned next month must
     * not change what a job already being cut includes. NULL means the house
     * rule decided, and `revisionsLeft` reads it that way.
     *
     * The contract is why this is a column at all: § 6 says "o ile Zlecenie nie
     * stanowi inaczej", and a Zlecenie that cannot say otherwise makes that
     * clause unprovable.
     */
    revisionsIncluded: smallint('revisions_included'),

    /**
     * The technical spec the finished file must meet (Zalacznik 1 poz. 7).
     * Snapshotted from the variant; an Admin may overwrite it on one line.
     */
    exportSpec: text('export_spec'),

    /**
     * Third-party material allowed on this line — music, stock, fonts
     * (Zalacznik 1 poz. 9). Snapshotted from the variant.
     */
    thirdPartyMaterials: text('third_party_materials'),

    /**
     * Anything this ORDER demands that the catalogue cannot know: a
     * confidentiality requirement, a publication embargo, a restriction the end
     * client imposed (Zalacznik 1 poz. 11).
     *
     * Line-only, with no variant default, and that is the distinction it
     * carries: the three above are properties of what was SOLD and come from
     * the catalogue, while this is a property of who it was sold TO.
     */
    specialTerms: text('special_terms'),

    /**
     * The pause half of "ZASADA GLOBALNA: PAUZA ZEGARA REALIZACJI" (spec §1).
     * The performer's deadline only counts time the job is actually with
     * them, so every window where the ball is with the client — awaiting
     * approval, awaiting a film-length decision — is banked here instead of
     * eating their clock. `clockPausedAt` is the open interval (NULL = running),
     * `pausedMs` the closed total. A4 owns the arithmetic; this is storage.
     */
    clockPausedAt: timestamp('clock_paused_at', { withTimezone: true }),
    pausedMs: bigint('paused_ms', { mode: 'number' }).notNull().default(0),

    /**
     * When the two-hour escalation fired for this line (spec §6, E2).
     *
     * A timestamp and not a boolean, and it exists purely so the sweep does not
     * repeat itself: the job runs every fifteen minutes, and a line inside its
     * last two hours matches the predicate on every single pass. Without this
     * the performer gets the same warning eight times before the deadline they
     * were being warned about.
     *
     * Nothing moves `due_at` today (it is stamped once, at assignment), so
     * nothing clears this yet. G2 gives a revision its own 12h deadline — and
     * whatever writes that MUST null this in the same statement, or the line
     * will never be warned about the new deadline because it was already warned
     * about the old one.
     */
    escalatedAt: timestamp('escalated_at', { withTimezone: true }),

    /**
     * Per-product fulfilment flags that must not each earn a column, e.g.
     * `badMaterial` (spec §7) which halts auto-acceptance. Kept separate from
     * `metadata` above on purpose: that one freezes what was BOUGHT, this one
     * tracks what is HAPPENING, and mixing them makes an order line unreadable.
     */
    flags: jsonb('flags').$type<Record<string, unknown>>(),

    /**
     * This line IS the coordinator (spec §13 · H3).
     *
     * A capability column rather than a magic label or a `kind`, for the reason
     * every other one in this schema is: something has to be true of the ROW
     * that a query can filter on and a CHECK can reason about. Three things are
     * true of it and of nothing else — it names no offering (there is no
     * catalogue row for "12 % of your basket"), it is never split per piece,
     * and it may legitimately cost nothing, because spec §13 makes the
     * coordinator free from eleven positions.
     *
     * It is still a real fulfilment track: somebody holds the order together,
     * and H4 hangs both of §13's chats off this row. What it never has is a
     * brief, source material, revisions or a deadline of its own — coordination
     * finishes when the work it co-ordinates does.
     */
    isCoordinator: boolean('is_coordinator').notNull().default(false),

    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => [
    index('order_item_order_idx').on(t.orderId, t.sortOrder),
    index('order_item_offering_idx').on(t.offeringId),
    // "Which lines are waiting on this one?" — asked every time a film settles
    // and on every cascade, always by the parent's id.
    index('order_item_depends_on_idx').on(t.dependsOnItemId),
    index('order_item_variant_idx').on(t.offeringVariantId),
    // One order, at most one coordinator. A partial unique index rather than a
    // service-layer promise, because the failure it prevents is a double
    // charge: two checkouts of the same basket, or a retry that got halfway.
    uniqueIndex('order_item_one_coordinator').on(t.orderId).where(sql`${t.isCoordinator}`),

    // Same price/quote pair as every catalogue table — see `services.ts`.
    check('order_item_price_quote_xor', sql`(${t.unitPriceNetMinor} IS NULL) = ${t.isQuoteOnly}`),
    // `> 0`, with one exemption: spec §13's coordinator is FREE from eleven
    // positions, and a zero there is as deliberate and as traceable as the
    // 100% rung `order_item_total_nonneg` allows below. Everything else with a
    // price has to have a real one — a silent 0 is a line nobody charged for.
    check(
      'order_item_price_positive',
      sql`${t.unitPriceNetMinor} IS NULL OR ${t.unitPriceNetMinor} > 0 OR ${t.isCoordinator}`,
    ),
    // The other half of `order.hasQuoteOnlyItems`: a quote-only line is
    // excluded from every total, so it must not be able to CARRY one. Without
    // this, a QUOTE line with a `line_total_net_minor` would be summed by P5's
    // engine and would also earn the basket a MULTI_SERVICE rung it did not
    // buy — the "Wycena indywidualna → 0 zł" bug from the other direction.
    check('order_item_quote_no_total', sql`NOT ${t.isQuoteOnly} OR ${t.lineTotalNetMinor} IS NULL`),
    // Zero is not a rate somebody agreed — see the column's note.
    check(
      'order_item_performer_rate_positive',
      sql`${t.performerRateNetMinor} IS NULL OR ${t.performerRateNetMinor} > 0`,
    ),
    // …and the other direction, which is the one that costs money. The check
    // above only forbids a quote line from CARRYING a total; on its own it
    // leaves a PRICED line free to carry none, and a NULL line total is
    // silently dropped by every `SUM()` — a paid line that vanishes from the
    // order total instead of failing loudly. Together the two make
    // `line_total_net_minor IS NULL` exactly equivalent to `is_quote_only`,
    // the same equivalence `order_item_price_quote_xor` gives the unit price.
    check(
      'order_item_priced_has_total',
      sql`${t.isQuoteOnly} OR ${t.lineTotalNetMinor} IS NOT NULL`,
    ),
    // `>= 0`, not `> 0`: a 100% rung is a deliberate, traceable zero, unlike a
    // missing unit price. The unit-price check above is what blocks the latter.
    check(
      'order_item_total_nonneg',
      sql`${t.lineTotalNetMinor} IS NULL OR ${t.lineTotalNetMinor} >= 0`,
    ),
    check('order_item_quantity_positive', sql`${t.quantity} > 0`),
    // A cancellation fee exists only on a cancelled line, and zero is not a fee
    // — NULL already says "nothing is owed". Without the first half a bug could
    // put a payout on live work and have it counted twice: once by the month's
    // close as a finished job, once again as a cancellation.
    check(
      'order_item_cancellation_payout_valid',
      sql`${t.cancellationPayoutNetMinor} IS NULL
          OR (${t.cancellationPayoutNetMinor} > 0 AND ${t.status} = 'CANCELLED')`,
    ),
    // The amount and the month it falls in are one fact. A payout without a
    // date could never be closed into a statement; a date without an amount
    // would be a month claiming a fee that does not exist.
    check(
      'order_item_cancellation_payout_dated',
      sql`(${t.cancellationPayoutNetMinor} IS NULL) = (${t.cancellationPayoutAt} IS NULL)`,
    ),
    check('order_item_discount_range', sql`${t.discountBps} >= 0 AND ${t.discountBps} <= 10000`),
    check('order_item_tax_rate_range', sql`${t.taxRateBps} >= 0 AND ${t.taxRateBps} <= 10000`),

    // A variant is a refinement of an offering, never an alternative to it: a
    // line that names a package without naming what the package is OF cannot be
    // read back, priced or refunded. The five-way "exactly one of these keys"
    // check this replaced existed only because there were five catalogues.
    check(
      'order_item_variant_needs_offering',
      sql`${t.offeringVariantId} IS NULL OR ${t.offeringId} IS NOT NULL`,
    ),

    // The coordinator is a charge on the ORDER, not a thing from the
    // catalogue: there is no offering row for "12 % of your basket", and one
    // invented to hold the fee would show up in the browser as something to
    // buy on its own. It is also always exactly one — a second coordinator is
    // a bug in the checkout, and `order_item_one_coordinator` below is where
    // that stops being possible.
    check(
      'order_item_coordinator_has_no_offering',
      sql`NOT ${t.isCoordinator} OR (${t.offeringId} IS NULL AND ${t.quantity} = 1)`,
    ),

    // ── the line's own track ──────────────────────────────────────────────
    // Two reads dominate the fulfilment side and neither can afford a scan:
    // the 15-minute escalation sweep ("who is within 2h of a deadline?", E2)
    // and a performer opening their four tabs (A5).
    index('order_item_status_due_idx').on(t.status, t.dueAt),
    index('order_item_assignee_idx').on(t.assignedUserId, t.status),

    // The (n+1)-th request accepts the work instead of spending a round, so the
    // ceiling is "rounds included, plus the one that closes the line". It used
    // to read 3 because every level included exactly two; now that a level can
    // be sold more, the ceiling follows `offering_variant`'s own limit of ten
    // rather than a number that would fail at the first sale of a third round.
    check(
      'order_item_revision_round_range',
      sql`${t.revisionRound} >= 0 AND ${t.revisionRound} <= 11`,
    ),
    /** A backstop, not the policy — see the column. */
    check('order_item_defect_claims_range', sql`${t.defectClaims} BETWEEN 0 AND 20`),
    /**
     * A declaration is an instant and a person, or neither. A date with nobody
     * behind it is a record that cannot be read back to anybody, which is the
     * one thing this column exists to be.
     */
    check(
      'order_item_copies_deleted_paired',
      sql`(${t.copiesDeletedAt} IS NULL) = (${t.copiesDeletedByUserId} IS NULL)`,
    ),
    // Same range as the variant it is copied from — two CHECKs that disagreed
    // would make a level insertable and its order line not.
    check(
      'order_item_revisions_included_range',
      sql`${t.revisionsIncluded} IS NULL
          OR (${t.revisionsIncluded} >= 0 AND ${t.revisionsIncluded} <= 10)`,
    ),
    // Banked pause can only accumulate. A negative total would hand the
    // performer a deadline EARLIER than the one they agreed to.
    check('order_item_paused_ms_nonneg', sql`${t.pausedMs} >= 0`),
    // Accepting work nobody delivered is not a state the product has — not
    // even for auto-acceptance, which fires off the delivery timestamp. The
    // implication is one-directional: delivered-but-not-accepted is the whole
    // point of the acceptance window.
    check(
      'order_item_accepted_needs_delivery',
      sql`${t.acceptedAt} IS NULL OR ${t.deliveredAt} IS NOT NULL`,
    ),
  ],
)

export type Order = typeof order.$inferSelect
export type NewOrder = typeof order.$inferInsert
export type OrderItem = typeof orderItem.$inferSelect
export type NewOrderItem = typeof orderItem.$inferInsert
